import { getBrand } from '../utils/branding'

const SUPPORTED_LANGUAGES = ['uk', 'ru', 'en', 'pl']
const STORAGE_KEY = 'client_lang'
const DEFAULT_LANGUAGE = 'uk'
const I18N_BASE = `${(import.meta.env.BASE_URL || '').replace(/\/$/, '')}/i18n`

// Накладаємо бренд поверх свіжозавантаженого словника.
// JSON-файли — legacy «СКТ Глобус», тож на cloudGPS-доменах глибоким обходом
// замінюємо всі брендові токени на нейтральний бренд (cloudGPS).
// Так не доводиться плодити по 4 копії i18n-файлів на кожен бренд і нові
// рядки автоматично потрапляють під заміну без правок коду.
//
// Що ловимо:
//   "СКТ «Глобус»" / "СКТ Глобус" / "FMS Globus" / "SKT «Globus»" / "SKT „Globus”" → cloudGPS
//   "системі Глобус" / "системе Глобус" / "Globus system" / "systemie Globus" → ... cloudGPS
//   "Глобус" / "Globus" standalone — НЕ ловимо, бо це чіпляє технічні placeholders
//     (напр. "globus123" як приклад логіну Hydra) і доменні згадки.
//   "globus.cloudgps.online" → "client.cloudgps.online" — підміна публічних URL у FAQ.
function applyBrandOverrides(dict) {
  const brand = getBrand()
  if (brand.id === 'globus') return dict
  const name = brand.name
  const replacements = [
    [/СКТ\s«Глобус»/g, name],
    [/СКТ\sГлобус/g, name],
    [/SKT\s«Globus»/g, name],
    [/SKT\s„Globus”/g, name], // польська типографіка в pl.json (лапки „…”)
    [/FMS\sGlobus/g, name],
    [/системі\sГлобус/g, `системі ${name}`],
    [/систему\sГлобус/g, `систему ${name}`],
    [/системе\sГлобус/g, `системе ${name}`],
    [/системи\sГлобус/g, `системи ${name}`],
    [/systemie\sGlobus/g, `systemie ${name}`],
    [/Globus\ssystem/gi, `${name} system`],
    [/globus\.cloudgps\.online/g, 'client.cloudgps.online'],
  ]
  const apply = (s) => replacements.reduce((acc, [re, val]) => acc.replace(re, val), s)
  const walk = (node) => {
    if (typeof node === 'string') return apply(node)
    if (Array.isArray(node)) return node.map(walk)
    if (node && typeof node === 'object') {
      for (const k of Object.keys(node)) node[k] = walk(node[k])
    }
    return node
  }
  return walk(dict)
}

// Все три — обычные модульные переменные, без Vue реактивности.
// Язык в этой системе меняется только при входе (с перезагрузкой страницы),
// так что никаких реактивных потребителей нет. Vue reactive здесь был бы чистым
// оверхедом — t() вызывается тысячи раз (на каждый маркер в buildMarkerIcon).
let lang = DEFAULT_LANGUAGE
let messages = {}
let ready = false

// Guard від undefined/null ключа: одна крива запись не повинна валити цілий рендер
const resolvePath = (m, key) =>
  typeof key === 'string'
    ? key.split('.').reduce((acc, segment) => (acc && acc[segment] ? acc[segment] : null), m)
    : null

export const availableLanguages = SUPPORTED_LANGUAGES

export const getLanguage = () => lang

export const isI18nReady = () => ready

export function t(key, values = {}) {
  if (typeof key !== 'string' || !key) return ''
  const message = resolvePath(messages, key)
  if (typeof message !== 'string') return message ?? key

  const tokens = Object.keys(values)
  if (!tokens.length) return message
  return tokens.reduce(
    (acc, token) => acc.replaceAll(`{{${token}}}`, values[token]),
    message
  )
}

export function translateApiError(errorCode, fallback = null) {
  if (!errorCode) return fallback || t('common.error') || 'Error'
  const translated = resolvePath(messages, `apiErrors.${errorCode}`)
  if (translated) return translated
  return fallback || errorCode
}

async function fetchTranslations(targetLang) {
  const cacheBuster = typeof __BUILD_TS__ !== 'undefined' ? __BUILD_TS__ : Date.now()
  const url = `${I18N_BASE}/${targetLang}.json?v=${cacheBuster}`
  let data
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    data = await response.json()
  } catch (e) {
    // Обрезанный ответ мог прилипнуть в HTTP/SW-кэше (кэш-бастер одинаков для всего
    // билда) — тогда JSON.parse падает при каждой загрузке до ручной очистки кэша.
    // Повторяем с принудительным обходом кэша, чтобы портал не умирал белым экраном.
    // Паузы обязательны: вторая причина обрыва — момент подмены словника на сервере
    // при деплое, и мгновенный ретрай попадает в то же окно записи.
    let lastError = e
    for (const delay of [300, 1200]) {
      await new Promise((resolve) => setTimeout(resolve, delay))
      try {
        const retry = await fetch(`${url}&r=${Date.now()}`, { cache: 'reload' })
        if (!retry.ok) throw new Error(`HTTP ${retry.status}`)
        data = await retry.json()
        lastError = null
        break
      } catch (retryError) {
        lastError = retryError
      }
    }
    if (lastError) throw new Error(`Не удалось загрузить словарь ${targetLang}: ${lastError.message}`)
  }
  messages = applyBrandOverrides(data)
  ready = true
}

export async function setLanguage(value) {
  const targetLang = SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE
  await fetchTranslations(targetLang)
  lang = targetLang
  localStorage.setItem(STORAGE_KEY, targetLang)
  document.documentElement.lang = targetLang
}

// Маппинг ISO кодов стран → язык интерфейса для авто-детекта по IP.
// Не входящие в маппинг страны получают английский.
const COUNTRY_TO_LANG = {
  PL: 'pl',
  UA: 'uk',
  RU: 'ru',
  BY: 'ru',
  KZ: 'ru',
}

// Берём первый язык из navigator.languages, входящий в SUPPORTED_LANGUAGES.
// Учитываем порядок предпочтений пользователя (pl-PL,en-US → pl).
function detectLanguageFromBrowser() {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language || '']
  for (const tag of tags) {
    const code = String(tag).split('-')[0].toLowerCase()
    if (SUPPORTED_LANGUAGES.includes(code)) return code
  }
  return null
}

// Fallback по IP — только если браузер не сообщил поддерживаемый язык.
// Таймаут 2с, чтобы не блокировать загрузку логина при недоступности API.
async function detectLanguageFromIp() {
  try {
    const res = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(2000),
    })
    if (!res.ok) return null
    const data = await res.json()
    const country = String(data.country_code || data.country || '').toUpperCase()
    if (!country) return null
    return COUNTRY_TO_LANG[country] || 'en'
  } catch {
    return null
  }
}

// White label: бренд із сервера може приїхати ПІСЛЯ першого завантаження
// словника (перший візит на партнерський домен). Перезбираємо словник —
// applyBrandOverrides незворотній (токени вже замінені), тому перечитуємо
// сирий JSON (браузерний кеш той самий v= — це безкоштовно).
if (typeof window !== 'undefined') {
  window.addEventListener('wl-brand-ready', () => {
    if (ready) fetchTranslations(lang).catch(() => { /* залишаємо поточний словник */ })
  })
}

export async function bootstrapI18n() {
  const savedLang = localStorage.getItem(STORAGE_KEY)
  if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
    await setLanguage(savedLang)
    return
  }
  // Авто-детект только при первом заходе (когда в localStorage нет явного выбора).
  // Сначала смотрим navigator.languages — это бесплатно и точнее IP (без VPN/роуминга).
  // Если ни один язык браузера не поддерживается — спрашиваем геолокацию по IP.
  let detected = detectLanguageFromBrowser()
  if (!detected) detected = await detectLanguageFromIp()
  await setLanguage(detected || DEFAULT_LANGUAGE)
}
